import { useQueryClient } from '@tanstack/react-query';

import { useUIContext } from 'lib/context/UIContext/UIContext';
import { ia, mString } from 'lib/helpers/utility';
import useModal from 'lib/hooks/useModal';
import useScrollBlock from 'lib/hooks/useScrollBlock';

import ProcessPayment from 'components/Patient/checkin/SecurePayment/components/ProcessPayment';
import { PaymentContextProvider } from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';
import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Button/Button';

import SuccessPayment from '../../SuccessPayment';

const SecurePayment = ({ amountDue, practice, patient, token, onSuccess, invoice }) => {
  const { isOpen, openModal, closeModal } = useModal();
  const queryClient = useQueryClient();
  const { device } = useUIContext();
  const isMobile = device === 'tablet-sm' || device === 'mobile';

  const [blockScroll, allowScroll] = useScrollBlock();

  const onSuccessHandler = (data) => {
    onSuccess(data);
    queryClient.invalidateQueries(['invoice', token]);
  };

  const onError = () => {
    allowScroll();

    showAlert({
      color: 'danger',
      variant: 'lighter',
      title: 'Payment Failed',
      message: 'Payment failed. Please try again.',
      icon: 'danger-mono'
    });
  };

  const onFailHandler = (error) => {
    allowScroll();
    closeModal();

    const errorMessage =
      (ia(error) ? error.join(' ') : error) || 'Payment failed. Please try again.';

    showAlert({
      color: 'danger',
      variant: 'filled',
      title: 'Payment Failed',
      message: errorMessage,
      icon: 'error-filled'
    });
  };

  const openModalHandler = () => {
    blockScroll();
    openModal();
  };

  const closeModalHandler = () => {
    allowScroll();
    closeModal();
  };

  const isDisabled = invoice?.paid || amountDue <= 0;

  const getTooltipMessage = () => {
    if (invoice?.paid) {
      return 'This invoice has already been paid';
    }
    if (amountDue <= 0) {
      return 'No amount due';
    }
    return '';
  };

  return (
    <PaymentContextProvider
      amount={amountDue}
      invoiceToken={token}
      patientId={patient?.id}
      practiceId={practice?.id}
      onSuccess={onSuccessHandler}
      onError={onError}
      initialMethod="credit-card">
      <div className="absolute bottom-0 left-0 right-0 flex flex-col items-end justify-center gap-2 border-0 border-t border-solid border-t-neutral-200 bg-white px-8 py-4">
        <Button
          variant="primary"
          size={isMobile ? 'small' : 'medium'}
          text={`Pay ${mString(amountDue)}`}
          onClick={openModalHandler}
          isDisabled={isDisabled}
          tooltip={isDisabled ? getTooltipMessage() : ''}
        />
      </div>
      <ProcessPayment
        isOpen={isOpen}
        handleClose={closeModalHandler}
        invoice={invoice}
        patientId={patient?.id}
        onSuccess={onSuccessHandler}
        onFail={onFailHandler}
        practice={practice}
        amount={amountDue}
        SuccessScreen={SuccessPayment}
        token={token}
      />
    </PaymentContextProvider>
  );
};

export default SecurePayment;
